import { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import { Button } from './ui/button';
import Logo from './Logo';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isInStandaloneMode = () => {
      return (
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone ||
        document.referrer.includes('android-app://')
      );
    };

    setIsStandalone(isInStandaloneMode());

    // Check if iOS
    const isIOSDevice = () => {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    };

    setIsIOS(isIOSDevice());

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('installPromptDismissed');
    const dismissedTime = localStorage.getItem('installPromptDismissedTime');
    
    // Show prompt again after 7 days
    if (dismissed && dismissedTime) {
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - parseInt(dismissedTime) < sevenDaysInMs) {
        return;
      }
    }

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 3 seconds
      setTimeout(() => {
        if (!isInStandaloneMode()) {
          setShowPrompt(true);
        }
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show prompt after 5 seconds if not already installed
    if (isIOSDevice() && !isInStandaloneMode()) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt && !isIOS) return;

    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('installPromptDismissed', 'true');
    localStorage.setItem('installPromptDismissedTime', Date.now().toString());
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-2xl border border-gray-100">
        <div className="p-6 relative">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-stone-400 hover:text-stone-500"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Logo size="lg" />
            </div>
            
            <div className="flex-1">
              <h3 className="text-lg font-serif font-light text-black mb-1">
                Install Ophilia
              </h3>
              <p className="text-sm font-sans text-stone-500 mb-4">
                Add Ophilia to your home screen for quick access and a better experience.
              </p>
              
              {isIOS ? (
                <div className="text-xs font-sans text-stone-500 space-y-2">
                  <p className="font-medium text-stone-600">To install:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Tap the share button <span className="inline-block">⎋</span></li>
                    <li>Scroll down and tap "Add to Home Screen"</li>
                    <li>Tap "Add" in the top right</li>
                  </ol>
                </div>
              ) : (
                <Button
                  onClick={handleInstallClick}
                  className="w-full bg-black hover:bg-[#FF7A45] text-[#F5F1E8] font-sans font-medium uppercase tracking-wider text-sm transition-all duration-300"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Install App
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
